import { Heading, Icon, Text } from '@afterpaytouch/core'
import React, { FC, ReactNode, useState } from 'react'

import { useAppRouter, useInitialData, useIsCheckoutLoadingOrUpdating, useIsSingleUseCard, useMerchantAvatarUrl, useMerchantId } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { Money } from '../Money'

import styles from './OrderSummaryVariationDStyle.module.css'
import { OrderConversionBreakdown } from '../OrderConversionBreakdown'
import { AddressModeType } from '../../model'
import { ModuleContainer } from '../ModuleContainer'
import { OrderDetails } from '../OrderDetails'
import { generateMerchantNameAbbr } from '../../utils/merchant'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { Money as MoneyType } from '@afterpay/types'
interface SummaryHeaderVariationDProps {
  onPressAccordion: () => void
  expanded: boolean
  merchantLogoUrl?: string | null
  summaryHeaderText?: string
  summaryAmount?: MoneyType
  shipmentSectionRenderer: () => ReactNode
  promoSectionRenderer: () => ReactNode
  testNameSpace?: string
  merchantId?: number
  contentInDetail: boolean
}

interface HeaderIconProps {
  expanded: boolean
  shouldDisplayIcon: boolean
}

const NOT_A_MERCHANT = -1

const HeaderIcon: FC<HeaderIconProps> = ({ expanded, shouldDisplayIcon }) => {
  if (!shouldDisplayIcon) {
    return null
  }

  return expanded ? (
    <Icon.CaretUp data-testid='summary-experiment-d-up' size='ExtraSmall' title='Collapse' role='img' />
  ) : (
    <Icon.CaretDown data-testid='summary-experiment-d-down' size='ExtraSmall' title='Expand' role='img' />
  )
}

export const OrderSummaryVariationD: FC<SummaryHeaderVariationDProps> = ({
  onPressAccordion,
  expanded,
  summaryHeaderText,
  summaryAmount,
  shipmentSectionRenderer,
  promoSectionRenderer,
  contentInDetail,
}) => {
  const avatarUrl = useMerchantAvatarUrl()
  const merchantId = useMerchantId() ?? NOT_A_MERCHANT
  const { sectionHeadingSize } = useCheckoutSizes()
  const isSup = useIsSingleUseCard()
  const isCheckoutLoadingOrUpdating = useIsCheckoutLoadingOrUpdating()
  const { locale } = useAppRouter()
  const { addressMode } = useInitialData()
  if (merchantId === NOT_A_MERCHANT) {
    return null
  }
  const hasAvatar = !isUndefinedOrEmpty(avatarUrl)
  const shouldDisplayAvatar = !isSup && hasAvatar
  const logoUrl = hasAvatar ? `https:${avatarUrl}` : ''
  const placeholderText = generateMerchantNameAbbr(summaryHeaderText)
  const handlePressAccordion = (): void => {
    if (!contentInDetail) {
      return
    }
    onPressAccordion()
  }

  return (
    <ModuleContainer disabled={!contentInDetail} data-testid='summary-experiment-d-container' className={styles.summaryContainer}>
      <button data-testid='summary-experiment-d-button' onClick={handlePressAccordion} className={styles.summaryHeaderContainer}>
        <div className={styles.summaryHeaderWrapper}>
          {shouldDisplayAvatar ? (
            <ImageWithFallback
              data-testid='experiment-merchant-logo'
              alt='merchant-logo'
              src={logoUrl}
              width={40}
              height={40}
              className={styles.summaryHeaderMerchantLogo}
              fallback={<MerchantAvatarFallback placeholderText={placeholderText} />}
            />
          ) : (
            <MerchantAvatarFallback placeholderText={placeholderText} />
          )}
          <Heading size={sectionHeadingSize} testNameSpace='summary-merchant-display-name'>
            {summaryHeaderText}
          </Heading>
          <div className={styles.summaryAmountWrapper}>
            <Money locale={locale} size={sectionHeadingSize} bold value={summaryAmount} loading={isCheckoutLoadingOrUpdating} />
            <HeaderIcon expanded={expanded} shouldDisplayIcon={contentInDetail} />
          </div>
        </div>
        <div className={styles.summaryHeaderExtraInfo}>
          {addressMode !== AddressModeType.NO_ADDRESS && shipmentSectionRenderer()}
          <OrderConversionBreakdown location='summary' />
          {promoSectionRenderer()}
        </div>
      </button>
      {contentInDetail && expanded && (
        <div className={styles.summaryExpansionContainer}>
          <OrderDetails />
        </div>
      )}
    </ModuleContainer>
  )
}

export const ImageWithFallback: FC<
  { src: string; fallback: ReactNode; alt: string } & React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
> = ({ src, fallback, alt, ...restProps }) => {
  const [isLoadingImageError, setIsLoadingImageError] = useState(false)
  const onImageLoadError = (): void => {
    setIsLoadingImageError(true)
  }

  return isLoadingImageError ? fallback : <img src={src} alt={alt} onError={onImageLoadError} {...restProps} />
}

export const MerchantAvatarFallback: FC<{ placeholderText: string }> = ({ placeholderText }) => {
  return (
    <div data-testid='experiment-merchant-logo-fallback' className={styles.summaryHeaderMerchantLogoFallback}>
      <Text size='S' renderAs='span'>
        {placeholderText}
      </Text>
    </div>
  )
}
